import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  enabled: false,
  host: "",
  port: 587,
  security: "starttls",
  username: "",
  from_email: "",
  from_name: "",
  notify_email: "",
};

const SECURITY_OPTIONS = [
  { value: "starttls", label: "STARTTLS (zwykle port 587)" },
  { value: "ssl", label: "SSL/TLS (zwykle port 465)" },
  { value: "none", label: "Bez szyfrowania (niezalecane)" },
];

export default function SmtpSettings({ onHealthChange }) {
  const [cfg, setCfg] = useState(EMPTY);
  const [health, setHealth] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/smtp");
      const { password_set, last_error, last_error_at, last_success_at, failed_notifications, ...rest } = data;
      setCfg(rest);
      setHealth({ password_set, last_error, last_error_at, last_success_at, failed_notifications });
      setTestTo(rest.notify_email || "");
      onHealthChange?.(data);
    } catch (e) {
      toast.error("Nie udało się pobrać ustawień");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // password: null = zostaw zapisane haslo bez zmian
      const payload = { ...cfg, port: Number(cfg.port), password: passwordTouched ? password : null };
      await api.put("/admin/settings/smtp", payload);
      toast.success("Ustawienia zapisane");
      setPassword("");
      setPasswordTouched(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/settings/smtp/test", { to: testTo });
      toast.success(`Wiadomość testowa wysłana na ${data.sent_to}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <p className="text-[#5C6670] py-12 text-center">Wczytywanie ustawień…</p>;

  return (
    <div data-testid="smtp-settings" className="max-w-3xl space-y-8">
      {health?.last_error && (
        <div className="border border-[#C88E75] bg-[#FBF3EF] rounded-2xl p-5" data-testid="smtp-error-box">
          <p className="text-overline text-[#C88E75] mb-2">Ostatnia wysyłka nie powiodła się</p>
          <p className="text-sm text-[#2B2B2B] font-mono break-all">{health.last_error}</p>
          {health.last_error_at && (
            <p className="text-xs text-[#5C6670] mt-2">
              {new Date(health.last_error_at).toLocaleString("pl-PL")}
            </p>
          )}
        </div>
      )}

      <div>
        <h2 className="font-display text-2xl text-[#2B2B2B] mb-2">Powiadomienia e-mail</h2>
        <p className="text-sm text-[#5C6670] leading-relaxed">
          Zgłoszenia z formularzy zawsze zapisują się w panelu. Poniższa konfiguracja decyduje tylko o tym,
          czy dodatkowo przychodzi o nich powiadomienie na skrzynkę.
        </p>
      </div>

      <SmtpHelp />

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox" checked={cfg.enabled} data-testid="smtp-enabled"
          onChange={(e) => set("enabled", e.target.checked)}
          className="w-4 h-4 accent-[#8A9A86]"
        />
        <span className="text-sm text-[#2B2B2B]">Wysyłaj powiadomienia o nowych zgłoszeniach</span>
      </label>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Serwer SMTP" hint="np. smtp.gmail.com">
          <Input value={cfg.host} onChange={(v) => set("host", v)} testId="smtp-host" placeholder="smtp.gmail.com" />
        </Field>
        <Field label="Port">
          <Input value={cfg.port} onChange={(v) => set("port", v)} testId="smtp-port" type="number" />
        </Field>
        <Field label="Szyfrowanie">
          <select
            value={cfg.security} onChange={(e) => set("security", e.target.value)} data-testid="smtp-security"
            className="w-full px-4 py-2.5 rounded-xl border border-[#E5E0D8] bg-white text-sm text-[#2B2B2B]"
          >
            {SECURITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Login" hint="Zwykle pełny adres e-mail">
          <Input value={cfg.username} onChange={(v) => set("username", v)} testId="smtp-username" />
        </Field>

        <Field
          label="Hasło"
          hint={
            health?.password_set
              ? "Hasło jest zapisane. Zostaw puste, aby go nie zmieniać."
              : "Dla Gmaila: hasło aplikacji (16 znaków), nie hasło do konta."
          }
        >
          <Input
            type="password" value={password} testId="smtp-password"
            placeholder={health?.password_set ? "•••••••••••• (zapisane)" : ""}
            onChange={(v) => { setPassword(v); setPasswordTouched(true); }}
          />
        </Field>
        <Field label="Nazwa nadawcy" hint="Widoczna w polu Od">
          <Input value={cfg.from_name} onChange={(v) => set("from_name", v)} testId="smtp-from-name" placeholder="Bezpieczna Przestrzeń" />
        </Field>
        <Field label="Adres nadawcy" hint="Dla Gmaila musi być tym samym adresem co login">
          <Input value={cfg.from_email} onChange={(v) => set("from_email", v)} testId="smtp-from-email" />
        </Field>
        <Field label="Adres powiadomień" hint="Tu trafią zgłoszenia z formularzy">
          <Input value={cfg.notify_email} onChange={(v) => set("notify_email", v)} testId="smtp-notify-email" />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          onClick={save} disabled={saving} data-testid="smtp-save"
          className="px-6 py-2.5 rounded-xl bg-[#2B2B2B] text-white text-sm disabled:opacity-50"
        >
          {saving ? "Zapisywanie…" : "Zapisz ustawienia"}
        </button>
      </div>

      <div className="border-t border-[#E5E0D8] pt-8">
        <h3 className="font-display text-lg text-[#2B2B2B] mb-2">Wyślij wiadomość testową</h3>
        <p className="text-sm text-[#5C6670] mb-4">
          Test używa zapisanych ustawień, więc najpierw je zapisz. Działa także przy wyłączonych powiadomieniach.
        </p>
        <div className="flex flex-wrap gap-3">
          <Input value={testTo} onChange={setTestTo} testId="smtp-test-to" placeholder="adres@example.com" className="flex-1 min-w-[240px]" />
          <button
            onClick={sendTest} disabled={testing} data-testid="smtp-test"
            className="px-6 py-2.5 rounded-xl border border-[#2B2B2B] text-[#2B2B2B] text-sm disabled:opacity-50"
          >
            {testing ? "Wysyłanie…" : "Wyślij test"}
          </button>
        </div>
        {health?.last_success_at && (
          <p className="text-xs text-[#5C6670] mt-3">
            Ostatnia udana wysyłka: {new Date(health.last_success_at).toLocaleString("pl-PL")}
          </p>
        )}
      </div>
    </div>
  );
}

function SmtpHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E5E0D8] rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="smtp-help-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-sm text-[#2B2B2B]">Jak to wypełnić</span>
        <span className="text-[#8A9A86] text-sm shrink-0">{open ? "zwiń" : "rozwiń"}</span>
      </button>

      {open && (
        <div className="px-5 pb-6 space-y-6 border-t border-[#E5E0D8] pt-5" data-testid="smtp-help-body">
          <p className="text-sm text-[#2B2B2B] leading-relaxed">
            Powiadomienia wysyłane są z Twojej własnej skrzynki pocztowej — panel tylko się do niej loguje.
            Potrzebujesz danych tej skrzynki. Poniżej ustawienia dwóch najczęstszych dostawców.
          </p>

          <div>
            <p className="text-overline mb-3">Poczta o2 / Onet (adres kończący się na op.pl, o2.pl, onet.pl)</p>
            <dl className="text-sm text-[#2B2B2B] space-y-1.5">
              <HelpRow k="Serwer SMTP" v="smtp.poczta.onet.pl" />
              <HelpRow k="Port" v="465" />
              <HelpRow k="Szyfrowanie" v="SSL/TLS" />
              <HelpRow k="Login" v="pełny adres e-mail, np. imie.nazwisko@op.pl" />
              <HelpRow k="Hasło" v="zwykłe hasło do skrzynki" />
            </dl>
          </div>

          <div>
            <p className="text-overline mb-3">Gmail</p>
            <dl className="text-sm text-[#2B2B2B] space-y-1.5">
              <HelpRow k="Serwer SMTP" v="smtp.gmail.com" />
              <HelpRow k="Port" v="587" />
              <HelpRow k="Szyfrowanie" v="STARTTLS" />
              <HelpRow k="Login" v="pełny adres Gmail" />
              <HelpRow k="Hasło" v="hasło aplikacji, nie hasło do konta Google" />
            </dl>
            <p className="text-sm text-[#5C6670] leading-relaxed mt-3">
              Gmail nie wpuści panelu na zwykłe hasło. W ustawieniach konta Google włącz weryfikację dwuetapową,
              a następnie w sekcji Hasła aplikacji wygeneruj osobne hasło dla tej strony. Będzie miało szesnaście
              znaków. Wpisz je poniżej — nikt poza Tobą go nie zobaczy.
            </p>
          </div>

          <div className="border-t border-[#E5E0D8] pt-5">
            <p className="text-overline mb-3">Dwie zasady, które łatwo przeoczyć</p>
            <ul className="space-y-2 pl-5">
              <li className="text-sm text-[#2B2B2B] leading-relaxed list-disc marker:text-[#8A9A86]">
                Adres nadawcy musi być tym samym adresem, co login. Serwery pocztowe odrzucają wiadomości
                wysyłane rzekomo z cudzego adresu.
              </li>
              <li className="text-sm text-[#2B2B2B] leading-relaxed list-disc marker:text-[#8A9A86]">
                Adres powiadomień to skrzynka, na którą mają przychodzić zgłoszenia. Może być ta sama co nadawca —
                wtedy piszesz sama do siebie i tak to zwykle działa.
              </li>
            </ul>
          </div>

          <div className="border-t border-[#E5E0D8] pt-5">
            <p className="text-overline mb-3">Kolejność</p>
            <ol className="space-y-2 pl-5">
              {[
                "Wypełnij pola i kliknij Zapisz ustawienia.",
                "Kliknij Wyślij test i sprawdź skrzynkę. Test działa także przy wyłączonych powiadomieniach.",
                "Gdy wiadomość testowa dojdzie, zaznacz Wysyłaj powiadomienia i zapisz ponownie.",
              ].map((t, i) => (
                <li key={i} className="text-sm text-[#2B2B2B] leading-relaxed list-decimal marker:text-[#8A9A86]">
                  {t}
                </li>
              ))}
            </ol>
            <p className="text-sm text-[#5C6670] leading-relaxed mt-3">
              Jeśli test się nie powiedzie, panel pokaże dokładną odpowiedź serwera pocztowego. Komunikat o nieprzyjętym
              loginie lub haśle oznacza zwykle literówkę albo — przy Gmailu — użycie hasła do konta zamiast hasła aplikacji.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function HelpRow({ k, v }) {
  return (
    <div className="flex flex-wrap gap-x-3">
      <dt className="text-[#5C6670] min-w-[130px]">{k}</dt>
      <dd className="font-mono text-[#2B2B2B]">{v}</dd>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-overline mb-2">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#5C6670] mt-1.5 leading-relaxed">{hint}</p>}
    </div>
  );
}

function Input({ value, onChange, testId, type = "text", placeholder = "", className = "" }) {
  return (
    <input
      type={type} value={value} placeholder={placeholder} data-testid={testId}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-4 py-2.5 rounded-xl border border-[#E5E0D8] bg-white text-sm text-[#2B2B2B] placeholder:text-[#B5AFA5] ${className}`}
    />
  );
}
